import {
    Checkbox,
    Colors,
    DraggableList,
    DraggableListItem,
    Icon,
    Text,
    type DraggableItem,
} from '@amelio/ui-library';

import QuestionDraggableOption from '../QuestionDraggableOption/QuestionDraggableOption';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage, type LocalizedText } from '@/utils/localization';

export type CheckboxOption = {
    label: LocalizedText;
};

export type CheckboxOptionListProps = {
    options: DraggableItem<CheckboxOption>[];
    setOptions: (options: DraggableItem<CheckboxOption>[]) => void;
    updateOption: (id: string, value: string) => void;
    deleteOption: (id: string) => void;
    addOption: (index: number) => void;
    handleOtherChange: (checked: boolean) => void;
    isOtherOption: boolean;
    hideOtherOption?: boolean;
};

const CheckboxOptionList = ({
    options,
    setOptions,
    updateOption,
    deleteOption,
    addOption,
    handleOtherChange,
    isOtherOption,
    hideOtherOption,
}: CheckboxOptionListProps) => {
    const { t } = useAmelioTranslations();
    const companyLanguage = useSelectedAppLanguage();

    const handleReorder = (items: DraggableItem<CheckboxOption>[]) => {
        setOptions(items);
    };

    return (
        <div>
            <Text
                className={'mt-8'}
                weight={'bold'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.checkbox.longTitle')}
            </Text>

            <Text
                className={'mt-2'}
                weight={'light'}
                size={'small'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.checkbox.description')}
            </Text>

            <div className={'mt-6 max-w-80'}>
                <DraggableList
                    items={options}
                    onReorder={handleReorder}
                    useDragHandle
                    renderItem={(item, meta) => (
                        <DraggableListItem isDragging={meta.isDragging}>
                            <QuestionDraggableOption
                                value={getTextByLanguage(item.data.label, companyLanguage)}
                                dataTestId={`checkbox-option-${meta.index}`}
                                onChange={(value) => updateOption(item.id, value)}
                                onRemove={() => deleteOption(item.id)}
                                canAddNewOption
                                inputWidth={'qdo-input'}
                                isLastOption={meta.index === options.length - 1}
                                onAdd={() => addOption(meta.index)}
                            />
                        </DraggableListItem>
                    )}
                />

                {options.length === 0 && (
                    <button
                        type={'button'}
                        onClick={() => addOption(0)}
                        className={'cursor-pointer'}
                        aria-label={'Add option'}
                        data-testid={'checkbox-option-empty-add'}
                    >
                        <Icon
                            name={'essential-add-circle'}
                            className={'h-2 w-2'}
                            color={Colors.BASE}
                        />
                    </button>
                )}

                {!hideOtherOption && (
                    <div className={'mt-4'}>
                        <Checkbox
                            dataTestId={'other-option-checkbox'}
                            label={t(
                                'evaluation360Questionnaire.questionForm.questions.common.otherOptionLabel',
                            )}
                            checked={isOtherOption}
                            onChange={handleOtherChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckboxOptionList;
